import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import packageService, { CreatePackagePlanDto } from '../../services/packageService';
import { PackagePlan, ClientPackage, PackageStats } from '../../types';

interface PackageState {
  plans:     PackagePlan[];
  purchases: ClientPackage[];
  stats:     PackageStats | null;
  total:     number;
  loading:   boolean;
  error:     string | null;
}

const initialState: PackageState = {
  plans: [], purchases: [], stats: null, total: 0, loading: false, error: null,
};

const upsertPlan = (list: PackagePlan[], p: PackagePlan) => {
  const i = list.findIndex(x => x._id === p._id);
  if (i !== -1) list[i] = p; else list.unshift(p);
};

export const fetchPackageStats = createAsyncThunk('packages/stats', async (_, { rejectWithValue }) => { try { return await packageService.getStats(); } catch (e: unknown) { return rejectWithValue((e as Error).message); } });
export const fetchPackagePlans = createAsyncThunk('packages/fetchPlans', async (_, { rejectWithValue }) => { try { return await packageService.getPlans(); } catch (e: unknown) { return rejectWithValue((e as Error).message); } });
export const createPackagePlan = createAsyncThunk('packages/createPlan', async (d: CreatePackagePlanDto, { rejectWithValue }) => { try { return await packageService.createPlan(d); } catch (e: unknown) { return rejectWithValue((e as Error).message); } });
export const updatePackagePlan = createAsyncThunk('packages/updatePlan', async ({ id, data }: { id: string; data: Partial<CreatePackagePlanDto> }, { rejectWithValue }) => { try { return await packageService.updatePlan(id, data); } catch (e: unknown) { return rejectWithValue((e as Error).message); } });
export const deletePackagePlan = createAsyncThunk('packages/deletePlan', async (id: string, { rejectWithValue }) => { try { await packageService.deletePlan(id); return id; } catch (e: unknown) { return rejectWithValue((e as Error).message); } });
export const fetchPackagePurchases = createAsyncThunk('packages/fetchPurchases', async (p: { status?: string; page?: number } | undefined, { rejectWithValue }) => { try { return await packageService.getPurchases(p); } catch (e: unknown) { return rejectWithValue((e as Error).message); } });

const packageSlice = createSlice({
  name: 'packages', initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPackageStats.fulfilled, (s, a) => { s.stats = a.payload.data ?? null; })
      .addCase(fetchPackagePlans.pending,    (s) => { s.loading = true; })
      .addCase(fetchPackagePlans.fulfilled,  (s, a) => { s.loading = false; s.plans = a.payload.data ?? []; })
      .addCase(fetchPackagePlans.rejected,   (s, a) => { s.loading = false; s.error = a.payload as string; })
      .addCase(createPackagePlan.fulfilled,  (s, a) => { if (a.payload.data) upsertPlan(s.plans, a.payload.data); })
      .addCase(updatePackagePlan.fulfilled,  (s, a) => { if (a.payload.data) upsertPlan(s.plans, a.payload.data); })
      .addCase(deletePackagePlan.fulfilled,  (s, a: PayloadAction<string>) => { const p = s.plans.find(p => p._id === a.payload); if (p) p.isActive = false; })
      .addCase(fetchPackagePurchases.pending,  (s) => { s.loading = true; })
      .addCase(fetchPackagePurchases.fulfilled,(s, a) => { s.loading = false; s.purchases = a.payload.data ?? []; s.total = a.payload.total ?? 0; })
      .addCase(fetchPackagePurchases.rejected, (s, a) => { s.loading = false; s.error = a.payload as string; });
  },
});

export const { clearError } = packageSlice.actions;
export default packageSlice.reducer;
